import React, { useState, useEffect, useMemo } from 'react';
import { Property, AppUser } from '../../types';
import { XMarkIcon, SpinnerIcon, CheckCircleIcon, ExclamationTriangleIcon } from '../Icons';
import { ID_TYPE_MAPPING } from './ClientVisitConstants';
import { determineOwner, getPropertyShareUrl, getMinDateString, fetchVisitConfigForProperty } from './ClientVisitHelpers';
import { submitClientVisitBackendRequest } from '../../services/visitService';
import { VisitFormFields } from './VisitFormFields';
import { ClientVisitConfirmation } from './ClientVisitConfirmation';

interface ModalProps { isOpen: boolean; onClose: () => void; property: Property; appUser?: AppUser | null; }

const DAYS_ORDER = ["LUNES", "MARTES", "MIERCOLES", "JUEVES", "VIERNES", "SABADO", "DOMINGO"];

export const ClientScheduleVisitModal: React.FC<ModalProps> = ({ isOpen, onClose, property, appUser }) => {
  const [status, setStatus] = useState<'form' | 'confirmation' | 'loading' | 'success' | 'error'>('form');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [visitConfig, setVisitConfig] = useState<any>(null);

  const initialForm = useMemo(() => ({
    nombre_cliente: (appUser?.id === 'guest-client' || !appUser?.displayName) ? '' : appUser.displayName,
    email_cliente: appUser?.email || '', countryCode: '+57', telefono: appUser?.celular || '',
    tipoId: 'CC', numeroId: appUser?.numeroIdentificacion || '', fecha1: '', hora1: '', fecha2: '', hora2: '', Description: ''
  }), [appUser]);

  const [formData, setFormData] = useState(initialForm);

  useEffect(() => {
    if (isOpen) {
      setFormData(initialForm);
      setStatus('form');
      setErrors({});
      fetchVisitConfigForProperty(property).then(cfg => setVisitConfig(cfg));
    }
  }, [isOpen, initialForm, property]);

  if (!isOpen) return null;

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!formData.nombre_cliente.trim()) errs.nombre_cliente = "El nombre completo es obligatorio.";
    if (!formData.email_cliente.trim() || !/^\S+@\S+\.\S+$/.test(formData.email_cliente)) errs.email_cliente = "Ingresa un correo electrónico válido.";
    if (!formData.telefono.trim() || formData.telefono.length < 7) errs.telefono = "Ingresa un teléfono válido de al menos 7 dígitos.";
    if (!formData.numeroId.trim()) errs.numeroId = "El número de documento es obligatorio.";

    if (!formData.fecha1 || !formData.hora1) errs.fecha1 = "Debes seleccionar la fecha y hora de la Opción 1.";
    if (!formData.fecha2 || !formData.hora2) errs.fecha2 = "Debes seleccionar la fecha y hora de la Opción 2.";

    if (formData.fecha1 && formData.fecha2 && formData.fecha1 === formData.fecha2) {
      errs.fechas_iguales = "Las dos opciones de visita no pueden ser en la misma fecha. Selecciona días diferentes.";
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleNext = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) setStatus('confirmation');
  };

  const handleConfirmSubmit = async () => {
    setStatus('loading');
    const payload = {
      ...formData,
      tipoId: ID_TYPE_MAPPING[formData.tipoId] || formData.tipoId,
      telefono: `${formData.countryCode}${formData.telefono}`,
      ciudad: property.ciudad, codigo: property.codigo, direccion: property.direccion,
      owner: determineOwner(property.estrategiaComercial, property.owner),
      soloInteresado: 0, propertyOfInterestValueForm: property.precio_venta,
      propertyCodeForm: property.codigo, propertyTypeForm: property.tipoinmueble,
      cityForm: property.ciudad, localityForm: property.localidad, neighborhoodForm: property.neighborhoodName,
      urlProperty: getPropertyShareUrl(property),
      lineOfBusiness: 'Compra nuevo',
      lineOfBusinessForm: 'Compra nuevo',
      accountForm: 'Ciencuadras',
      fuente: 'Cliente Portal'
    };

    try {
      await submitClientVisitBackendRequest(payload);
      setStatus('success');
    } catch { setStatus('error'); }
  };

  return (
    <div className="fixed inset-0 bg-gray-900/80 backdrop-blur-sm z-[999] flex items-center justify-center p-3 sm:p-4">
      <div className="bg-white rounded-3xl sm:rounded-[2.5rem] shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden border border-gray-100 animate-fade-in-up">
        <header className="px-6 py-5 border-b border-gray-100 flex justify-between items-center bg-gray-50/50 flex-shrink-0">
          <div>
            <h3 className="text-base sm:text-lg font-black text-gray-800 uppercase tracking-tight">Agendar Visita</h3>
            <p className="text-[11px] text-sky-600 font-bold">Inmueble {property.codigo}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-full transition-colors"><XMarkIcon className="w-5 h-5 text-gray-400" /></button>
        </header>

        {status === 'loading' ? (
          <div className="flex-grow flex flex-col items-center justify-center p-16 text-center">
            <SpinnerIcon className="w-12 h-12 text-sky-600 animate-spin mb-4" />
            <p className="font-extrabold text-gray-700 text-xs uppercase tracking-widest">Enviando solicitud a Zoho CRM...</p>
          </div>
        ) : status === 'success' ? (
          <div className="flex-grow flex flex-col items-center justify-center p-12 text-center animate-fade-in">
            <CheckCircleIcon className="w-16 h-16 text-green-500 mb-4" />
            <h3 className="text-xl font-black text-gray-900 uppercase tracking-tight">¡Visita Agendada!</h3>
            <p className="mt-2 text-xs text-gray-600 font-medium max-w-xs">Nos comunicaremos contigo para confirmar los detalles de tu visita.</p>
            <button onClick={onClose} className="mt-6 px-10 py-3 bg-gray-900 text-white rounded-2xl font-black uppercase text-xs">Cerrar</button>
          </div>
        ) : status === 'error' ? (
          <div className="flex-grow flex flex-col items-center justify-center p-10 text-center animate-fade-in">
            <ExclamationTriangleIcon className="w-16 h-16 text-red-500 mb-4" />
            <h3 className="text-lg font-black text-gray-800 uppercase">Error al procesar agendamiento</h3>
            <p className="mt-2 text-xs text-gray-600">No se pudo registrar la solicitud. Notificación enviada a soporte.</p>
            <button onClick={() => setStatus('form')} className="mt-6 px-10 py-3 bg-sky-600 text-white rounded-2xl font-black uppercase text-xs">Reintentar</button>
          </div>
        ) : status === 'confirmation' ? (
          <ClientVisitConfirmation formData={formData} onSubmit={handleConfirmSubmit} onBack={() => setStatus('form')} loading={status === 'loading'} />
        ) : (
          <form onSubmit={handleNext} className="flex flex-col flex-grow overflow-hidden">
            <VisitFormFields formData={formData} setFormData={setFormData} visitConfig={visitConfig || { DIAS_ANTICIPACION: 2, HORARIO: {} }} minDate={getMinDateString()} DAYS_ORDER={DAYS_ORDER} errors={errors} />
            <footer className="px-6 py-4 border-t border-gray-100 flex justify-between items-center bg-gray-50/50 flex-shrink-0">
              <button type="button" onClick={onClose} className="px-4 py-2.5 text-xs font-black text-gray-400 hover:text-gray-700 uppercase tracking-wider">Cancelar</button>
              <button type="submit" className="px-8 py-3 bg-sky-600 text-white rounded-2xl font-black uppercase text-xs shadow-md shadow-sky-100 hover:bg-sky-700 transition-all">Siguiente</button>
            </footer>
          </form>
        )}
      </div>
    </div>
  );
};
